import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "@phosphor-icons/react";
import { api, resolveImg } from "../lib/api";
import { Reveal } from "../lib/motion";
import { Eyebrow } from "../components/ui-kit";

export default function Insights() {
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api
      .get("/articles")
      .then((res) => setArticles(res.data))
      .catch(() => setArticles([]))
      .finally(() => setLoading(false));
  }, []);

  const [featured, ...rest] = articles;

  return (
    <div data-testid="insights-page">
      <section className="bg-cloud pt-40 pb-20 lg:pt-48 lg:pb-24">
        <div className="mx-auto max-w-[1320px] px-6 lg:px-10">
          <Reveal>
            <Eyebrow>Insights</Eyebrow>
            <h1 className="mt-7 max-w-4xl font-heading text-5xl font-light leading-[1.02] tracking-tight text-navy sm:text-6xl lg:text-[4.2rem]">
              Field notes on building{" "}
              <span className="font-semibold text-gold">companies that last.</span>
            </h1>
          </Reveal>
        </div>
      </section>

      <section className="bg-white py-16 lg:py-24">
        <div className="mx-auto max-w-[1320px] px-6 lg:px-10">
          {loading ? (
            <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-3">
              {Array.from({ length: 6 }).map((_, i) => (
                <div key={i} className="animate-pulse">
                  <div className="aspect-[16/10] rounded-2xl bg-slate-100" />
                  <div className="mt-4 h-4 w-2/3 rounded bg-slate-100" />
                  <div className="mt-3 h-4 w-full rounded bg-slate-100" />
                </div>
              ))}
            </div>
          ) : (
            <>
              {featured && (
                <Reveal>
                  <Link
                    to={`/insights/${featured.slug}`}
                    data-testid="featured-article"
                    className="group grid gap-8 overflow-hidden rounded-[28px] border border-slate-100 bg-cloud/50 lg:grid-cols-2"
                  >
                    <div className="overflow-hidden">
                      <img
                        src={resolveImg(featured.cover_image)}
                        alt={featured.title}
                        className="h-full min-h-[280px] w-full object-cover transition-transform duration-700 group-hover:scale-105"
                      />
                    </div>
                    <div className="flex flex-col justify-center p-10 lg:p-14">
                      <span className="text-xs font-bold uppercase tracking-[0.22em] text-teal">
                        {featured.category} · {featured.read_time}
                      </span>
                      <h2 className="mt-5 font-heading text-3xl font-semibold leading-tight tracking-tight text-navy sm:text-4xl">
                        {featured.title}
                      </h2>
                      <p className="mt-5 text-lg leading-relaxed text-slate-600">
                        {featured.excerpt}
                      </p>
                      <span className="mt-7 inline-flex items-center gap-2 font-semibold text-navy transition-colors duration-300 group-hover:text-gold">
                        Read More <ArrowUpRight size={17} weight="bold" />
                      </span>
                    </div>
                  </Link>
                </Reveal>
              )}

              <div className="mt-10 grid gap-8 md:grid-cols-2 lg:grid-cols-3">
                {rest.map((a, i) => (
                  <Reveal key={a.id} delay={(i % 3) * 0.08}>
                    <Link
                      to={`/insights/${a.slug}`}
                      data-testid={`article-card-${a.slug}`}
                      className="group flex h-full flex-col overflow-hidden rounded-2xl border border-slate-100 bg-white transition-all duration-500 hover:-translate-y-1.5 hover:shadow-[0_24px_60px_rgba(11,31,58,0.10)]"
                    >
                      <div className="overflow-hidden">
                        <img
                          src={resolveImg(a.cover_image)}
                          alt={a.title}
                          className="aspect-[16/10] w-full object-cover transition-transform duration-700 group-hover:scale-105"
                        />
                      </div>
                      <div className="flex flex-1 flex-col p-7">
                        <span className="text-xs font-bold uppercase tracking-[0.2em] text-teal">
                          {a.category} · {a.read_time}
                        </span>
                        <h3 className="mt-3 font-heading text-xl font-semibold leading-snug text-navy">
                          {a.title}
                        </h3>
                        <p className="mt-3 flex-1 leading-relaxed text-slate-500">{a.excerpt}</p>
                        <span className="mt-5 inline-flex items-center gap-2 text-sm font-semibold text-navy transition-colors duration-300 group-hover:text-gold">
                          Read More <ArrowUpRight size={15} weight="bold" />
                        </span>
                      </div>
                    </Link>
                  </Reveal>
                ))}
              </div>
            </>
          )}
        </div>
      </section>
    </div>
  );
}
